import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import downIcon from "../../../assets/down-icon.svg";
import mergeIcon from "../../../assets/merge.svg";
import deleteIcon from "../../../assets/delete-red.svg";
import addButtonSymbol from "../../../assets/addButtonSymbol.svg";
import { FilterDropdown } from "./FilterDropdown";
import ConfirmRemoveMapping from "./ConfirmRemoveMapping";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";

export default function ProcedureDocumentMapping() {
  const location = useLocation();
  const prefillAppliedRef = useRef(false);

  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [procedureList, setProcedureList] = useState([]);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState("");
  const [showFetched, setShowFetched] = useState(false);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [documentNameList, setDocumentNameList] = useState([]);
  const [loadingDocumentNames, setLoadingDocumentNames] = useState(false);
  const [selectedDocumentName, setSelectedDocumentName] = useState("");

  const [admissionDocuments, setAdmissionDocuments] = useState([]);
  const [enhancementDocuments, setEnhancementDocuments] = useState([]);
  const [dischargeDocuments, setDischargeDocuments] = useState([]);
  const [loadingMappedDocs, setLoadingMappedDocs] = useState(false);

  const [openAddDocumentModal, setOpenAddDocumentModal] = useState(false);
  const [addDocumentSection, setAddDocumentSection] = useState(null);
  const [selectedDocumentsForAdd, setSelectedDocumentsForAdd] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);
  const [savingFromHeader, setSavingFromHeader] = useState(false);
  const [savingFreeze, setSavingFreeze] = useState(false);

  /** Pending documents selected from header (Document Name) to be mapped on Save. Each: { doc, sectionKey }. */
  const [pendingDocumentsToMap, setPendingDocumentsToMap] = useState([]);

  /** Fetch and Copy section: independent filters (same as header but separate state). */
  const [fetchCopyScheme, setFetchCopyScheme] = useState("");
  const [fetchCopySpecialty, setFetchCopySpecialty] = useState("");
  const [fetchCopyProcedureList, setFetchCopyProcedureList] = useState([]);
  const [loadingFetchCopyProcedures, setLoadingFetchCopyProcedures] = useState(false);
  const [fetchCopyProcedure, setFetchCopyProcedure] = useState("");
  const [fetchCopyDocumentType, setFetchCopyDocumentType] = useState("");
  const [fetchCopyDocumentNameList, setFetchCopyDocumentNameList] = useState([]);
  const [loadingFetchCopyDocumentNames, setLoadingFetchCopyDocumentNames] = useState(false);
  const [fetchCopyDocumentName, setFetchCopyDocumentName] = useState("");

  /** Right column: documents fetched by "Fetch and copy" filters. */
  const [fetchCopyAdmissionDocuments, setFetchCopyAdmissionDocuments] = useState([]);
  const [fetchCopyEnhancementDocuments, setFetchCopyEnhancementDocuments] = useState([]);
  const [fetchCopyDischargeDocuments, setFetchCopyDischargeDocuments] = useState([]);
  const [loadingFetchCopyDocs, setLoadingFetchCopyDocs] = useState(false);

  /** Right column: which documents are selected for merge (Set of doc id per section). */
  const [selectedFetchCopyAdmissionIds, setSelectedFetchCopyAdmissionIds] = useState(() => new Set());
  const [selectedFetchCopyEnhancementIds, setSelectedFetchCopyEnhancementIds] = useState(() => new Set());
  const [selectedFetchCopyDischargeIds, setSelectedFetchCopyDischargeIds] = useState(() => new Set());

  /** Remove mapping: confirm modal and API (update-doctype-procedure-mapping with status INACTIVE). */
  const [openConfirmRemoveModal, setOpenConfirmRemoveModal] = useState(false);
  const [selectedToRemove, setSelectedToRemove] = useState([]); // [{ sectionKey, index }]
  const [removingMapping, setRemovingMapping] = useState(false);

  const fetchSchemes = async () => {
    if (schemes.length > 0) return;
    try {
      setLoadingSchemes(true);
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultSchemeTypes({
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const total = apiData?.total ?? (Array.isArray(apiData?.data) ? apiData.data.length : 0);
      const totalPages =
        apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
      let list = Array.isArray(apiData?.data) ? apiData.data : [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultSchemeTypes({
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.data) ? res.data.data : [];
          list = list.concat(pageList);
        });
      }
      setSchemes(list);
    } catch (err) {
      console.error("Error fetching schemes:", err);
      setSchemes([]);
    } finally {
      setLoadingSchemes(false);
    }
  };

  const fetchSpecialties = async () => {
    if (specialties.length > 0) return;
    try {
      setLoadingSpecialties(true);
      const response = await ProcedureFlowServices.getSpecialityTypes({
        limit: 5000,
      });
      setSpecialties(
        Array.isArray(response?.data?.list) ? response.data.list : [],
      );
    } catch (err) {
      console.error("Error fetching specialties:", err);
      setSpecialties([]);
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const fetchDocumentType = async () => {
    try {
      setLoadingDocumentTypes(true);
      const response = await ProcedureFlowServices.getMasterDataList("documentType");
      const list = response?.data?.masterData?.allmasterdata || [];
      setDocumentTypes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching document type", error);
      setDocumentTypes([]);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  const fetchDocumentNamesByType = async (documentType) => {
    if (!documentType || String(documentType).trim() === "") {
      setDocumentNameList([]);
      return;
    }
    setLoadingDocumentNames(true);
    try {
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultDocumentNameListV2({
        status: "ACTIVE",
        documentType: String(documentType).trim(),
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const total = apiData?.total ?? 0;
      const totalPages =
        apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
      let list = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultDocumentNameListV2({
              status: "ACTIVE",
              documentType: String(documentType).trim(),
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.docNameData) ? res.data.docNameData : [];
          list = list.concat(pageList);
        });
      }
      setDocumentNameList(list);
    } catch (err) {
      console.error("Error fetching document name list:", err);
      setDocumentNameList([]);
    } finally {
      setLoadingDocumentNames(false);
    }
  };

  /** Fetch documents for all document types and merge (when user selects "All"). */
  const fetchAllDocumentNames = async () => {
    const types = documentTypes
      .map((d) => (typeof d === "string" ? d : d.value))
      .filter((v) => v && String(v).trim() !== "" && v !== "All");
    if (types.length === 0) {
      setDocumentNameList([]);
      return;
    }
    setLoadingDocumentNames(true);
    try {
      const limit = 500;
      const fetchForType = async (documentType) => {
        let list = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await ProcedureFlowServices.getDefaultDocumentNameListV2({
            status: "ACTIVE",
            documentType: String(documentType).trim(),
            page,
            limit,
          });
          const apiData = res?.data;
          const pageList = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
          list = list.concat(
            pageList.map((d) => ({ ...d, documentType: d.documentType ?? documentType })),
          );
          const total = apiData?.total ?? 0;
          const totalPages = apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
          hasMore = page < totalPages && pageList.length >= limit;
          page += 1;
        }
        return list;
      };
      const results = await Promise.all(types.map((t) => fetchForType(t)));
      const merged = results.flat();
      const seen = new Set();
      const deduped = merged.filter((d) => {
        const id = d?.documentTypeMappingId ?? d?.id ?? d?.documentId ?? "";
        const key = id || `${d?.documentName ?? ""}|${d?.documentSubType ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setDocumentNameList(deduped);
    } catch (err) {
      console.error("Error fetching all document names:", err);
      setDocumentNameList([]);
    } finally {
      setLoadingDocumentNames(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
    fetchSpecialties();
    fetchDocumentType();
  }, []);

  /** Reset prefill ref when navigating with new state (e.g. from a different row) */
  useEffect(() => {
    if (location?.state?.scheme || location?.state?.procedureName) {
      prefillAppliedRef.current = false;
    }
  }, [location?.key, location?.state?.scheme, location?.state?.procedureName]);

  /** Prefill scheme and speciality from Manage Procedure page (when opened from a row) */
  useEffect(() => {
    const state = location?.state;
    if (!state?.scheme || !state?.specialtyName || prefillAppliedRef.current) return;
    if (schemes.length === 0 || specialties.length === 0) return;
    const schemeExists = schemes.some((s) => (typeof s === "string" ? s : s.name) === state.scheme);
    const specialtyExists = specialties.some(
      (s) => (typeof s === "string" ? s : s.speciality) === state.specialtyName
    );
    if (schemeExists && specialtyExists) {
      setSelectedScheme(state.scheme);
      setSelectedSpecialty(state.specialtyName);
      prefillAppliedRef.current = true;
    }
  }, [location?.state, schemes, specialties]);

  /** Prefill procedure once procedureList is loaded (after scheme/specialty prefill) */
  useEffect(() => {
    const state = location?.state;
    if (!state?.procedureName || !prefillAppliedRef.current) return;
    if (procedureList.length === 0 || selectedProcedure) return;
    const procedureExists = procedureList.some(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") === state.procedureName
    );
    if (procedureExists) {
      setSelectedProcedure(state.procedureName);
    }
  }, [location?.state, procedureList, selectedProcedure]);

  useEffect(() => {
    setDocumentNameList([]);
    setSelectedDocumentName("");
    if (!selectedDocumentType || String(selectedDocumentType).trim() === "") return;
    if (selectedDocumentType === "All") {
      fetchAllDocumentNames();
    } else {
      fetchDocumentNamesByType(selectedDocumentType);
    }
  }, [selectedDocumentType, documentTypes]);

  /** Fetch all procedures for scheme + speciality (no pagination – fetch all pages). */
  const fetchProceduresForSchemeAndSpeciality = async (schemeId, specialityId) => {
    if (!schemeId || !specialityId) {
      setProcedureList([]);
      return;
    }
    setLoadingProcedures(true);
    setProcedureList([]);
    setSelectedProcedure("");
    try {
      const limit = 500;
      let page = 1;
      let hasMore = true;
      let allList = [];
      while (hasMore) {
        const res = await ProcedureFlowServices.getSchemeProcedureList({
          schemeId,
          specialityId,
          page,
          limit,
          procedureSchemeData: "ACTIVE",
        });
        const list = Array.isArray(res?.data?.list) ? res.data.list : [];
        allList = allList.concat(list);
        hasMore = res?.data?.hasMore === true && list.length >= limit;
        page += 1;
      }
      setProcedureList(allList);
    } catch (err) {
      console.error("Error fetching procedure list:", err);
      setProcedureList([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  /** Normalize API response to array. default-procedure-mapped-documents returns { defaultprocedureDocuments: [...] }. */
  const normalizeProcedureDocList = (data) => {
    if (!data || typeof data !== "object") return [];
    if (Array.isArray(data)) return data;
    const raw =
      data.defaultprocedureDocuments ??
      data.list ??
      data.data ??
      (Array.isArray(data.defaultDocuments) ? data.defaultDocuments : []);
    return Array.isArray(raw) ? raw : [];
  };

  /** Filter list to items matching the given document type (item.documentType or item.documenttypemapping?.documentType). */
  const filterByDocumentType = (list, documentType) => {
    if (!Array.isArray(list) || !documentType) return [];
    const typeUpper = String(documentType).toUpperCase();
    return list.filter((item) => {
      const itemType =
        item?.documentType ?? item?.documenttypemapping?.documentType ?? "";
      return String(itemType).toUpperCase() === typeUpper;
    });
  };

  /** Normalize items for display: preserve full object and ensure documentName for Section. */
  const normalizeMappedDocsForDisplay = (list) => {
    return (Array.isArray(list) ? list : []).map((item) => {
      if (typeof item === "string") return item;
      const name =
        item?.documentName ??
        item?.name ??
        item?.documenttypemapping?.documentName ??
        item?.documentTypeMapping?.documentName ??
        "";
      return name ? { ...item, documentName: name } : item;
    }).filter((item) => (typeof item === "string" ? item : item?.documentName));
  };

  useEffect(() => {
    setProcedureList([]);
    setSelectedProcedure("");
    const schemeMatch = schemes.find(
      (s) => (typeof s === "string" ? s : s.name) === selectedScheme
    );
    const specialityMatch = specialties.find(
      (s) => (typeof s === "string" ? s : s.speciality) === selectedSpecialty
    );
    const schemeId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    if (schemeId && specialityId) {
      fetchProceduresForSchemeAndSpeciality(schemeId, specialityId);
    }
  }, [selectedScheme, selectedSpecialty]);

  /** Fetch admission, enhancement, discharge mapped docs for the selected procedure. */
  const fetchMappedDocsByFilters = async () => {
    if (!selectedProcedure || selectedProcedure.trim() === "") {
      setAdmissionDocuments([]);
      setEnhancementDocuments([]);
      setDischargeDocuments([]);
      return;
    }
    const procedureItem = procedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        selectedProcedure
    );
    const procedureSchemeId = procedureItem?.id;
    if (!procedureSchemeId) {
      setAdmissionDocuments([]);
      setEnhancementDocuments([]);
      setDischargeDocuments([]);
      return;
    }
    const ids = [String(procedureSchemeId)];
    setLoadingMappedDocs(true);
    try {
      let admissionList = [];
      let enhancementList = [];
      let dischargeList = [];

      const res = await ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids);
      const allList = normalizeProcedureDocList(res?.data ?? {});

      if (allList.length > 0) {
        admissionList = filterByDocumentType(allList, "ADMISSIONDOCUMENT");
        enhancementList = filterByDocumentType(allList, "DURINGTREATMENTDOCUMENT");
        dischargeList = filterByDocumentType(allList, "ONDISCHARGEDOCUMENT");
      } else {
        const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ADMISSIONDOCUMENT"),
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "DURINGTREATMENTDOCUMENT"),
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ONDISCHARGEDOCUMENT"),
        ]);
        admissionList = normalizeProcedureDocList(admissionRes?.data ?? {});
        enhancementList = normalizeProcedureDocList(enhancementRes?.data ?? {});
        dischargeList = normalizeProcedureDocList(dischargeRes?.data ?? {});
      }

      setAdmissionDocuments(normalizeMappedDocsForDisplay(admissionList));
      setEnhancementDocuments(normalizeMappedDocsForDisplay(enhancementList));
      setDischargeDocuments(normalizeMappedDocsForDisplay(dischargeList));
    } catch (err) {
      console.error("Error fetching default procedure mapped documents:", err);
      setAdmissionDocuments([]);
      setEnhancementDocuments([]);
      setDischargeDocuments([]);
      toast.error("Failed to fetch mapped documents.");
    } finally {
      setLoadingMappedDocs(false);
    }
  };

  /** Auto-load mapped documents when procedure is selected (no Fetch button needed). */
  useEffect(() => {
    setAdmissionDocuments([]);
    setEnhancementDocuments([]);
    setDischargeDocuments([]);

    if (!selectedProcedure || selectedProcedure.trim() === "") return;
    const procedureItem = procedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        selectedProcedure
    );
    if (!procedureItem?.id) return;

    let cancelled = false;
    const ids = [String(procedureItem.id)];
    const load = async () => {
      setLoadingMappedDocs(true);
      try {
        let admissionList = [];
        let enhancementList = [];
        let dischargeList = [];

        const res = await ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids);
        const allList = normalizeProcedureDocList(res?.data ?? {});

        if (allList.length > 0) {
          admissionList = filterByDocumentType(allList, "ADMISSIONDOCUMENT");
          enhancementList = filterByDocumentType(allList, "DURINGTREATMENTDOCUMENT");
          dischargeList = filterByDocumentType(allList, "ONDISCHARGEDOCUMENT");
        } else {
          const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
            ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ADMISSIONDOCUMENT"),
            ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "DURINGTREATMENTDOCUMENT"),
            ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ONDISCHARGEDOCUMENT"),
          ]);
          admissionList = normalizeProcedureDocList(admissionRes?.data ?? {});
          enhancementList = normalizeProcedureDocList(enhancementRes?.data ?? {});
          dischargeList = normalizeProcedureDocList(dischargeRes?.data ?? {});
        }

        if (!cancelled) {
          setAdmissionDocuments(normalizeMappedDocsForDisplay(admissionList));
          setEnhancementDocuments(normalizeMappedDocsForDisplay(enhancementList));
          setDischargeDocuments(normalizeMappedDocsForDisplay(dischargeList));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error auto-loading mapped documents:", err);
          setAdmissionDocuments([]);
          setEnhancementDocuments([]);
          setDischargeDocuments([]);
          toast.error("Failed to load mapped documents.");
        }
      } finally {
        if (!cancelled) setLoadingMappedDocs(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedProcedure, procedureList]);

  /** Fetch and Copy: load procedures when scheme/specialty change. */
  useEffect(() => {
    setFetchCopyProcedureList([]);
    setFetchCopyProcedure("");
    const schemeMatch = schemes.find(
      (s) => (typeof s === "string" ? s : s.name) === fetchCopyScheme
    );
    const specialityMatch = specialties.find(
      (s) => (typeof s === "string" ? s : s.speciality) === fetchCopySpecialty
    );
    const schemeId =
      schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
        ? String(schemeMatch.id)
        : "";
    const specialityId =
      specialityMatch &&
      typeof specialityMatch === "object" &&
      specialityMatch.specialityId
        ? String(specialityMatch.specialityId)
        : "";
    if (!schemeId || !specialityId) return;

    let cancelled = false;
    const load = async () => {
      setLoadingFetchCopyProcedures(true);
      try {
        const limit = 500;
        let page = 1;
        let hasMore = true;
        let allList = [];
        while (hasMore) {
          const res = await ProcedureFlowServices.getSchemeProcedureList({
            schemeId,
            specialityId,
            page,
            limit,
            procedureSchemeData: "ACTIVE",
          });
          const list = Array.isArray(res?.data?.list) ? res.data.list : [];
          allList = allList.concat(list);
          hasMore = res?.data?.hasMore === true && list.length >= limit;
          page += 1;
        }
        if (!cancelled) setFetchCopyProcedureList(allList);
      } catch (err) {
        if (!cancelled) setFetchCopyProcedureList([]);
      } finally {
        if (!cancelled) setLoadingFetchCopyProcedures(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchCopyScheme, fetchCopySpecialty, schemes, specialties]);

  /** Fetch and Copy: load document names when document type change. */
  useEffect(() => {
    setFetchCopyDocumentNameList([]);
    setFetchCopyDocumentName("");
    if (!fetchCopyDocumentType || String(fetchCopyDocumentType).trim() === "") return;

    let cancelled = false;
    const load = async () => {
      setLoadingFetchCopyDocumentNames(true);
      try {
        const types =
          fetchCopyDocumentType === "All"
            ? documentTypes
                .map((d) => (typeof d === "string" ? d : d.value))
                .filter((v) => v && String(v).trim() !== "" && v !== "All")
            : [String(fetchCopyDocumentType).trim()];

        if (types.length === 0 && fetchCopyDocumentType !== "All") {
          if (!cancelled) setFetchCopyDocumentNameList([]);
          return;
        }

        const fetchForType = async (documentType) => {
          const limit = 500;
          let list = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const res = await ProcedureFlowServices.getDefaultDocumentNameListV2({
              status: "ACTIVE",
              documentType: String(documentType).trim(),
              page,
              limit,
            });
            const apiData = res?.data;
            const pageList = Array.isArray(apiData?.docNameData) ? apiData.docNameData : [];
            list = list.concat(
              pageList.map((d) => ({ ...d, documentType: d.documentType ?? documentType })),
            );
            const total = apiData?.total ?? 0;
            const totalPages = apiData?.totalPages ?? Math.max(1, Math.ceil(total / limit));
            hasMore = page < totalPages && pageList.length >= limit;
            page += 1;
          }
          return list;
        };

        const results = await Promise.all(types.map((t) => fetchForType(t)));
        const merged = results.flat();
        const seen = new Set();
        const deduped = merged.filter((d) => {
          const id = d?.documentTypeMappingId ?? d?.id ?? d?.documentId ?? "";
          const key = id || `${d?.documentName ?? ""}|${d?.documentSubType ?? ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (!cancelled) setFetchCopyDocumentNameList(deduped);
      } catch (err) {
        if (!cancelled) setFetchCopyDocumentNameList([]);
      } finally {
        if (!cancelled) setLoadingFetchCopyDocumentNames(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchCopyDocumentType, documentTypes]);

  const getDocMappingId = (doc) =>
    String(
      (doc && typeof doc === "object"
        ? doc.documentTypeMappingId ??
          doc.id ??
          doc.documenttypemapping?.id ??
          doc.documentId ??
          ""
        : "") || "",
    );

  const mergeDocumentsById = (existing, toAdd) => {
    const getId = (doc) =>
      String(
        (typeof doc === "object" && doc !== null
          ? doc.documentTypeMappingId ??
            doc.documenttypemapping?.documentId ??
            doc.documentId ??
            doc.id ??
            doc.documentName ??
            doc.name ??
            ""
          : doc) || "",
      );

    const existingIds = new Set(existing.map(getId).filter(Boolean));
    const result = [...existing];

    toAdd.forEach((doc) => {
      const id = getId(doc);
      if (!id || existingIds.has(id)) return;
      result.push(doc);
      existingIds.add(id);
    });

    return result;
  };

  const getSectionKeyFromDocumentType = (documentType) => {
    if (!documentType || typeof documentType !== "string") return null;
    const t = documentType.toUpperCase();
    if (t === "ADMISSIONDOCUMENT") return "admission";
    if (t === "DURINGTREATMENTDOCUMENT") return "enhancement";
    if (t === "ONDISCHARGEDOCUMENT") return "discharge";
    return null;
  };

  const getDocumentTypeForSectionKey = (sectionKey) => {
    if (sectionKey === "admission") return "ADMISSIONDOCUMENT";
    if (sectionKey === "enhancement") return "DURINGTREATMENTDOCUMENT";
    if (sectionKey === "discharge") return "ONDISCHARGEDOCUMENT";
    return "";
  };

  /**
   * Determine if a section should be visible based on selected Document Type.
   * If no document type is selected, all sections are visible.
   * Otherwise, only the matching section is visible.
   */
  const shouldShowSection = (sectionKey) => {
    if (!selectedDocumentType) return true; // Show all if no filter selected
    const selectedSection = getSectionKeyFromDocumentType(selectedDocumentType);
    return selectedSection === sectionKey;
  };

  /** Check if a doc is already in the given section (admission/enhancement/discharge). */
  const isDocAlreadyInSection = (doc, sectionKey) => {
    const getId = (d) =>
      String(
        (d && typeof d === "object"
          ? d.documentTypeMappingId ?? d.id ?? d.documenttypemapping?.id ?? d.documentId ?? ""
          : "") || "",
      );
    const getLabel = (d) =>
      (d && typeof d === "object"
        ? d.documentName ?? d.name ?? d.documenttypemapping?.documentName ?? ""
        : "") || "";
    const getSubType = (d) =>
      (d && typeof d === "object"
        ? d.documentSubType ?? d.documenttypemapping?.documentSubType ?? ""
        : "") || "";
    const docId = getId(doc);
    const docName = getLabel(doc);
    const docSub = getSubType(doc);
    const section =
      sectionKey === "admission"
        ? admissionDocuments
        : sectionKey === "enhancement"
        ? enhancementDocuments
        : dischargeDocuments;
    return (section || []).some((d) => {
      if (docId && getId(d) === docId) return true;
      if (docName && getLabel(d) === docName && getSubType(d) === docSub) return true;
      return false;
    });
  };

  /** When user selects a document from header Document Name dropdown, add it to the "selected to map" list. */
  const handleDocumentNameSelect = (value) => {
    setSelectedDocumentName(value);
    if (!value || !selectedDocumentType) return;
    const docMatch = (documentNameList || []).find((d) => {
      const label =
        typeof d === "string" ? d : d?.documentName ?? d?.name ?? d?.value ?? "";
      return label === value;
    });
    if (!docMatch) return;
    const docType =
      selectedDocumentType === "All"
        ? docMatch?.documentType ?? docMatch?.documenttypemapping?.documentType ?? ""
        : selectedDocumentType;
    const sectionKey = getSectionKeyFromDocumentType(docType);
    if (!sectionKey) return;
    const id = getDocMappingId(docMatch);
    if (!id) return;
    if (isDocAlreadyInSection(docMatch, sectionKey)) {
      toast.warn("This document is already mapped with the selected scheme and speciality.");
      setSelectedDocumentName("");
      return;
    }
    setPendingDocumentsToMap((prev) => {
      if (prev.some((p) => getDocMappingId(p.doc) === id && p.sectionKey === sectionKey))
        return prev;
      return [...prev, { doc: docMatch, sectionKey }];
    });
    setSelectedDocumentName(""); // reset so user can select another document
  };

  /** Remove a document from the pending "selected to map" list. */
  const removePendingDocument = (index) => {
    setPendingDocumentsToMap((prev) => prev.filter((_, i) => i !== index));
    toast.info("Document removed from selection.");
  };

  /** Fetch documents using Fetch and Copy filters and show two-column layout. */
  const fetchFetchCopyMappedDocs = async () => {
    const procedureItem = fetchCopyProcedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        fetchCopyProcedure
    );
    const procedureSchemeId = procedureItem?.id;
    if (!procedureSchemeId) {
      toast.warn("Please select Scheme, Speciality and Procedure in the Fetch and Copy filters.");
      return;
    }

    setLoadingFetchCopyDocs(true);
    try {
      const ids = [String(procedureSchemeId)];
      const res = await ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids);
      const allList = normalizeProcedureDocList(res?.data ?? {});

      let admissionList = [];
      let enhancementList = [];
      let dischargeList = [];

      if (allList.length > 0) {
        admissionList = filterByDocumentType(allList, "ADMISSIONDOCUMENT");
        enhancementList = filterByDocumentType(allList, "DURINGTREATMENTDOCUMENT");
        dischargeList = filterByDocumentType(allList, "ONDISCHARGEDOCUMENT");
      } else {
        const [admissionRes, enhancementRes, dischargeRes] = await Promise.all([
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ADMISSIONDOCUMENT"),
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "DURINGTREATMENTDOCUMENT"),
          ProcedureFlowServices.getDefaultProcedureMappedDocuments(ids, "ONDISCHARGEDOCUMENT"),
        ]);
        admissionList = normalizeProcedureDocList(admissionRes?.data ?? {});
        enhancementList = normalizeProcedureDocList(enhancementRes?.data ?? {});
        dischargeList = normalizeProcedureDocList(dischargeRes?.data ?? {});
      }

      const admissionDisplay = normalizeMappedDocsForDisplay(admissionList);
      const enhancementDisplay = normalizeMappedDocsForDisplay(enhancementList);
      const dischargeDisplay = normalizeMappedDocsForDisplay(dischargeList);
      setFetchCopyAdmissionDocuments(admissionDisplay);
      setFetchCopyEnhancementDocuments(enhancementDisplay);
      setFetchCopyDischargeDocuments(dischargeDisplay);
      setSelectedFetchCopyAdmissionIds(new Set(admissionDisplay.map((d) => getDocMappingId(d)).filter(Boolean)));
      setSelectedFetchCopyEnhancementIds(new Set(enhancementDisplay.map((d) => getDocMappingId(d)).filter(Boolean)));
      setSelectedFetchCopyDischargeIds(new Set(dischargeDisplay.map((d) => getDocMappingId(d)).filter(Boolean)));
      setShowFetched(true);
      toast.success("Fetch and copy: documents loaded. You can select and merge to Default Mapped Documents.");
    } catch (err) {
      console.error("Error fetching documents from existing:", err);
      setFetchCopyAdmissionDocuments([]);
      setFetchCopyEnhancementDocuments([]);
      setFetchCopyDischargeDocuments([]);
      setSelectedFetchCopyAdmissionIds(new Set());
      setSelectedFetchCopyEnhancementIds(new Set());
      setSelectedFetchCopyDischargeIds(new Set());
      toast.error("Failed to fetch documents from existing.");
    } finally {
      setLoadingFetchCopyDocs(false);
    }
  };

  /** Reset right column when fetch-copy filters are cleared. */
  useEffect(() => {
    if (!fetchCopyProcedure || fetchCopyProcedure.trim() === "") {
      setFetchCopyAdmissionDocuments([]);
      setFetchCopyEnhancementDocuments([]);
      setFetchCopyDischargeDocuments([]);
      setSelectedFetchCopyAdmissionIds(new Set());
      setSelectedFetchCopyEnhancementIds(new Set());
      setSelectedFetchCopyDischargeIds(new Set());
      setShowFetched(false);
    }
  }, [fetchCopyProcedure]);

  /** Reset all filters and document states */
  const handleReset = () => {
    // Reset header filters
    setSelectedScheme("");
    setSelectedSpecialty(null);
    setSelectedProcedure("");
    setSelectedDocumentType("");
    setSelectedDocumentName("");
    setDocumentNameList([]);
    setProcedureList([]);
    
    // Reset mapped documents
    setAdmissionDocuments([]);
    setEnhancementDocuments([]);
    setDischargeDocuments([]);
    
    // Reset pending documents
    setPendingDocumentsToMap([]);
    
    // Reset fetch and copy filters
    setFetchCopyScheme("");
    setFetchCopySpecialty("");
    setFetchCopyProcedure("");
    setFetchCopyDocumentType("");
    setFetchCopyDocumentName("");
    setFetchCopyProcedureList([]);
    setFetchCopyDocumentNameList([]);
    
    // Reset fetch and copy documents
    setFetchCopyAdmissionDocuments([]);
    setFetchCopyEnhancementDocuments([]);
    setFetchCopyDischargeDocuments([]);
    
    // Reset selections
    setSelectedFetchCopyAdmissionIds(new Set());
    setSelectedFetchCopyEnhancementIds(new Set());
    setSelectedFetchCopyDischargeIds(new Set());
    
    // Reset layout
    setShowFetched(false);
    
    toast.info("All filters and documents have been reset.");
  };

  /** Copy one document from right column into the matching left-column section. */
  const handleCopyFromFetchCopy = (sectionKey, doc) => {
    if (sectionKey === "admission") {
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Admission.");
    } else if (sectionKey === "enhancement") {
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Enhancement.");
    } else if (sectionKey === "discharge") {
      setDischargeDocuments((prev) => mergeDocumentsById(prev, [doc]));
      toast.success("Document copied to Discharge / Claim.");
    }
  };

  /** Merge selected documents from one section (right column) into the matching left-column section. */
  const handleMergeSection = (sectionKey) => {
    let docsToMerge = [];
    if (sectionKey === "admission") {
      const ids = selectedFetchCopyAdmissionIds;
      docsToMerge = fetchCopyAdmissionDocuments.filter((d) => ids.has(getDocMappingId(d)));
      if (docsToMerge.length === 0) {
        toast.warning("No Admission documents selected to merge.");
        return;
      }
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, docsToMerge));
      toast.success("Selected Admission documents merged.");
    } else if (sectionKey === "enhancement") {
      const ids = selectedFetchCopyEnhancementIds;
      docsToMerge = fetchCopyEnhancementDocuments.filter((d) => ids.has(getDocMappingId(d)));
      if (docsToMerge.length === 0) {
        toast.warning("No Enhancement documents selected to merge.");
        return;
      }
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, docsToMerge));
      toast.success("Selected Enhancement documents merged.");
    } else if (sectionKey === "discharge") {
      const ids = selectedFetchCopyDischargeIds;
      docsToMerge = fetchCopyDischargeDocuments.filter((d) => ids.has(getDocMappingId(d)));
      if (docsToMerge.length === 0) {
        toast.warning("No Discharge / Claim documents selected to merge.");
        return;
      }
      setDischargeDocuments((prev) => mergeDocumentsById(prev, docsToMerge));
      toast.success("Selected Discharge / Claim documents merged.");
    }
  };

  /** Merge all selected documents from right column into left (all three sections). */
  const handleMergeAll = () => {
    const admissionSelected = fetchCopyAdmissionDocuments.filter((d) =>
      selectedFetchCopyAdmissionIds.has(getDocMappingId(d))
    );
    const enhancementSelected = fetchCopyEnhancementDocuments.filter((d) =>
      selectedFetchCopyEnhancementIds.has(getDocMappingId(d))
    );
    const dischargeSelected = fetchCopyDischargeDocuments.filter((d) =>
      selectedFetchCopyDischargeIds.has(getDocMappingId(d))
    );
    
    const totalSelected = admissionSelected.length + enhancementSelected.length + dischargeSelected.length;
    if (totalSelected === 0) {
      toast.warning("No documents selected to merge.");
      return;
    }
    
    setAdmissionDocuments((prev) => mergeDocumentsById(prev, admissionSelected));
    setEnhancementDocuments((prev) => mergeDocumentsById(prev, enhancementSelected));
    setDischargeDocuments((prev) => mergeDocumentsById(prev, dischargeSelected));
    toast.success("All selected documents merged (Admission, Enhancement, Discharge / Claim).");
  };

  /** Toggle selection of a document in the right column (Fetch and Copy). */
  const handleToggleFetchCopySelection = (sectionKey, doc) => {
    const id = getDocMappingId(doc);
    if (!id) return;
    if (sectionKey === "admission") {
      setSelectedFetchCopyAdmissionIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else if (sectionKey === "enhancement") {
      setSelectedFetchCopyEnhancementIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else if (sectionKey === "discharge") {
      setSelectedFetchCopyDischargeIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  /** Open confirm-remove modal for one document (sectionKey + index). */
  const handleDeleteFromSection = (sectionKey, index) => {
    setSelectedToRemove([{ sectionKey, index }]);
    setOpenConfirmRemoveModal(true);
  };

  /**
   * Confirm remove: call update-doctype-procedure-mapping with payload [{ id: "<uuid>", status: "INACTIVE" }].
   * The id is the mapping record id from default-procedure-mapped-documents response (defaultprocedureDocuments[].id).
   */
  const handleConfirmRemoveMapping = async () => {
    if (!Array.isArray(selectedToRemove) || selectedToRemove.length === 0) {
      setOpenConfirmRemoveModal(false);
      setSelectedToRemove([]);
      return;
    }

    const getList = (sectionKey) =>
      sectionKey === "admission"
        ? admissionDocuments
        : sectionKey === "enhancement"
          ? enhancementDocuments
          : dischargeDocuments;

    const docsToRemove = selectedToRemove.map(({ sectionKey, index }) => {
      const list = getList(sectionKey);
      const doc = Array.isArray(list) && list[index];
      return { sectionKey, index, doc };
    });

    const withMappingId = docsToRemove.filter(({ doc }) => doc && doc.id);
    const withoutMappingId = docsToRemove.filter(({ doc }) => !doc || !doc.id);

    setRemovingMapping(true);
    try {
      if (withMappingId.length > 0) {
        const deletePayload = withMappingId.map(({ doc }) => ({
          id: String(doc.id),
          status: "INACTIVE",
        }));
        await ProcedureFlowServices.updateDocTypeProcedureMapping(deletePayload, "mapping");
        toast.success("Document mapping removed successfully.");
      }
      if (withoutMappingId.length > 0) {
        toast.info("Some items were removed from the list only (no mapping id to deactivate).");
      }
      const indicesBySection = { admission: new Set(), enhancement: new Set(), discharge: new Set() };
      selectedToRemove.forEach(({ sectionKey, index }) => {
        if (indicesBySection[sectionKey]) indicesBySection[sectionKey].add(index);
      });
      setAdmissionDocuments((prev) =>
        prev.filter((_, i) => !indicesBySection.admission.has(i)),
      );
      setEnhancementDocuments((prev) =>
        prev.filter((_, i) => !indicesBySection.enhancement.has(i)),
      );
      setDischargeDocuments((prev) =>
        prev.filter((_, i) => !indicesBySection.discharge.has(i)),
      );
      setOpenConfirmRemoveModal(false);
      setSelectedToRemove([]);
    } catch (error) {
      console.error("Failed to remove procedure document mapping:", error);
      toast.error("Failed to remove mapping. Please try again.");
    } finally {
      setRemovingMapping(false);
    }
  };

  /**
   * Save all documents in the left column (Admission, Enhancement, Discharge/Claim) to the header-selected procedure.
   * Same as DefaultDocumentMappingPage handleSaveAllSections: include current section lists + any pending from header,
   * deduplicate by documentTypeMappingId, then send [{ procedureSchemeId, documentTypeMappingId }] so merged docs get mapped too.
   */
  const handleSaveMappingFromHeader = async () => {
    const procedureItem = procedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        selectedProcedure
    );
    const procedureSchemeId = procedureItem?.id;

    if (!procedureSchemeId) {
      toast.warn("Please select a Procedure before saving.");
      return;
    }

    /* Include all documents currently in the left column (including any merged from right) + pending from header */
    const pendingAdmission = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "admission").map((p) => p.doc);
    const pendingEnhancement = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "enhancement").map((p) => p.doc);
    const pendingDischarge = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "discharge").map((p) => p.doc);
    const allDocs = [
      ...(admissionDocuments || []),
      ...(enhancementDocuments || []),
      ...(dischargeDocuments || []),
      ...pendingAdmission,
      ...pendingEnhancement,
      ...pendingDischarge,
    ];

    /* Deduplicate by documentTypeMappingId so we don't send the same document twice */
    const seenIds = new Set();
    const uniqueDocs = allDocs.filter((doc) => {
      const id = getDocMappingId(doc);
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });

    const payload = uniqueDocs
      .map((doc) => {
        const id = getDocMappingId(doc);
        if (!id) return null;
        return {
          procedureSchemeId: String(procedureSchemeId),
          documentTypeMappingId: id,
          status: "ACTIVE",
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.warn("No documents to save. Add or copy documents to the lists first.");
      return;
    }

    setSavingFromHeader(true);
    try {
      const saveRes = await ProcedureFlowServices.updateDocTypeProcedureMapping(payload, "mapping");

      /* Merge pending into section state and clear pending (same as DefaultDocumentMappingPage) */
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, pendingAdmission));
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, pendingEnhancement));
      setDischargeDocuments((prev) => mergeDocumentsById(prev, pendingDischarge));
      setPendingDocumentsToMap([]);
      toast.success("Procedure document mapping saved successfully.");
    } catch (error) {
      console.error("Failed to save procedure-document mapping:", error);
      toast.error("Failed to save mapping. Please try again.");
    } finally {
      setSavingFromHeader(false);
    }
  };

  /**
   * Save and Freeze: runs independently from Save.
   * 1) Saves document mapping (same as Save).
   * 2) Refetches scheme-procedure-list to get procedureStage dynamically from API.
   * 3) Calls update-procedure-scheme-mapping with id, procedureId, procedureStage (from API).
   */
  const handleSaveAndFreeze = async () => {
    const procedureItem = procedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        selectedProcedure
    );
    const procedureSchemeId = procedureItem?.id;

    if (!procedureSchemeId) {
      toast.warn("Please select a Procedure first.");
      return;
    }

    const pendingAdmission = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "admission").map((p) => p.doc);
    const pendingEnhancement = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "enhancement").map((p) => p.doc);
    const pendingDischarge = (pendingDocumentsToMap || []).filter((p) => p.sectionKey === "discharge").map((p) => p.doc);
    const allDocs = [
      ...(admissionDocuments || []),
      ...(enhancementDocuments || []),
      ...(dischargeDocuments || []),
      ...pendingAdmission,
      ...pendingEnhancement,
      ...pendingDischarge,
    ];
    const seenIds = new Set();
    const uniqueDocs = allDocs.filter((doc) => {
      const id = getDocMappingId(doc);
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
    const payload = uniqueDocs
      .map((doc) => {
        const id = getDocMappingId(doc);
        if (!id) return null;
        return { 
          procedureSchemeId: String(procedureSchemeId), 
          documentTypeMappingId: id,
          status: "ACTIVE"
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.warn("No documents to save. Add or copy documents to the lists first.");
      return;
    }

    setSavingFreeze(true);
    try {
      await ProcedureFlowServices.updateDocTypeProcedureMapping(payload, "mapping");
      setAdmissionDocuments((prev) => mergeDocumentsById(prev, pendingAdmission));
      setEnhancementDocuments((prev) => mergeDocumentsById(prev, pendingEnhancement));
      setDischargeDocuments((prev) => mergeDocumentsById(prev, pendingDischarge));
      setPendingDocumentsToMap([]);

      const schemeMatch = schemes.find(
        (s) => (typeof s === "string" ? s : s.name) === selectedScheme
      );
      const specialityMatch = specialties.find(
        (s) => (typeof s === "string" ? s : s.speciality) === selectedSpecialty
      );
      const schemeId =
        schemeMatch && typeof schemeMatch === "object" && schemeMatch.id != null
          ? String(schemeMatch.id)
          : "";
      const specialityId =
        specialityMatch &&
        typeof specialityMatch === "object" &&
        specialityMatch.specialityId
          ? String(specialityMatch.specialityId)
          : "";
      if (!schemeId || !specialityId) {
        toast.success("Procedure document mapping saved successfully.");
        return;
      }

      const listRes = await ProcedureFlowServices.getSchemeProcedureList({
        schemeId,
        specialityId,
        limit: 500,
        page: 1,
      });
      const list = Array.isArray(listRes?.data?.list) ? listRes.data.list : [];
      const selectedItem = list.find(
        (item) =>
          (item?.proceduremaster?.procedure ?? item?.procedure ?? "") === selectedProcedure
      );
      if (!selectedItem) {
        toast.success("Procedure document mapping saved successfully.");
        return;
      }

      /* procedureStage fetched dynamically from scheme-procedure-list (e.g. DEFAULTMAPPING, FREEZED, etc.) */
      const procedureStage =
        selectedItem.procedureStage != null && String(selectedItem.procedureStage).trim() !== ""
          ? String(selectedItem.procedureStage).trim()
          : null;
      const procedureId =
        selectedItem?.proceduremaster?.procedureId ?? selectedItem?.procedureId ?? "";
      if (!procedureId || !selectedItem.id) {
        toast.success("Procedure document mapping saved successfully.");
        return;
      }
      if (procedureStage == null) {
        toast.warn("Procedure document mapping saved. Could not read procedureStage from API for freeze.");
        return;
      }

      const freezePayload = [
        {
          id: String(selectedItem.id),
          schemeId,
          procedureId: String(procedureId),
          procedureStage,
        },
      ];
      await ProcedureFlowServices.updateProcedureSchemeMapping(freezePayload);
      toast.success("Procedure document mapping saved and procedure stage updated successfully.");
    } catch (error) {
      console.error("Save and Freeze failed:", error);
      toast.error("Failed to save or update procedure stage. Please try again.");
    } finally {
      setSavingFreeze(false);
    }
  };

  const handleAddDocumentsToSection = async () => {
    if (!addDocumentSection || !Array.isArray(selectedDocumentsForAdd) || selectedDocumentsForAdd.length === 0) {
      toast.warn("Please select at least one document to map from the popup.");
      return;
    }

    const procedureItem = procedureList.find(
      (item) =>
        (item?.proceduremaster?.procedure ?? item?.procedure ?? "") ===
        selectedProcedure
    );
    const procedureSchemeId = procedureItem?.id;

    if (!procedureSchemeId) {
      toast.warn("Please select a Procedure first.");
      return;
    }

    const payload = selectedDocumentsForAdd
      .map((doc) => {
        const id = getDocMappingId(doc);
        if (!id) return null;
        return {
          procedureSchemeId: String(procedureSchemeId),
          documentTypeMappingId: id,
          status: "ACTIVE",
        };
      })
      .filter(Boolean);

    if (payload.length === 0) {
      toast.error("Selected documents are missing required ids.");
      return;
    }

    setSavingMapping(true);
    try {
      await ProcedureFlowServices.updateDocTypeProcedureMapping(payload, "mapping");

      const docsToAdd = selectedDocumentsForAdd;
      if (addDocumentSection === "admission") {
        setAdmissionDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      } else if (addDocumentSection === "enhancement") {
        setEnhancementDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      } else if (addDocumentSection === "discharge") {
        setDischargeDocuments((prev) => mergeDocumentsById(prev, docsToAdd));
      }

      setOpenAddDocumentModal(false);
      setAddDocumentSection(null);
      setSelectedDocumentsForAdd([]);
      const sectionLabel = addDocumentSection === "admission" ? "Admission" : addDocumentSection === "enhancement" ? "Enhancement" : "Discharge / Claim";
      toast.success(`Documents added to ${sectionLabel} successfully.`);
    } catch (error) {
      console.error("Failed to save procedure-document mapping from popup:", error);
      toast.error("Failed to add documents. Please try again.");
    } finally {
      setSavingMapping(false);
    }
  };

  const handleAddClickForSection = (sectionKey) => {
    const docType = getDocumentTypeForSectionKey(sectionKey);
    if (!docType) return;
    // When user clicks + on a section, preselect the matching Document Type
    // and open the Add popup so they can pick one or more Document Names and add them.
    setSelectedDocumentType(docType);
    setSelectedDocumentName("");
    setAddDocumentSection(sectionKey);
    setSelectedDocumentsForAdd([]);
    setOpenAddDocumentModal(true);
    const sectionLabel = sectionKey === "admission" ? "Admission" : sectionKey === "enhancement" ? "Enhancement" : "Discharge / Claim";
    toast.info(`Add document to ${sectionLabel}.`);
  };

  const schemeOptions = schemes.map((s) => (typeof s === "string" ? s : s.name));
  const specialityTypeOptions = specialties.map((s) =>
    typeof s === "string" ? s : s.speciality,
  );
  const specialityLabelMap = Object.fromEntries(
    specialties
      .filter((s) => typeof s === "object" && s != null && (s.speciality ?? s.name))
      .map((s) => {
        const name = s.speciality ?? s.name ?? "";
        const code = s.specialityId ?? s.id ?? "";
        const label = code ? `${code} - ${name}` : name;
        return [name, label];
      })
  );
  const documentTypeOptions = ["All", ...documentTypes.map((d) =>
    typeof d === "string" ? d : d.value,
  ).filter((v) => v && v !== "All")];
  const documentTypeLabelMap = {
    All: "All",
    ADMISSIONDOCUMENT: "ADMISSION",
    DURINGTREATMENTDOCUMENT: "ENHANCEMENT",
    ONDISCHARGEDOCUMENT: "DISCHARGE",
  };
  const documentNameOptions = documentNameList.map((d) =>
    typeof d === "string" ? d : d.documentName ?? d.name ?? d.value ?? "",
  ).filter(Boolean);

  const procedureOptions = procedureList.map((item) =>
    item?.proceduremaster?.procedure ?? item?.procedure ?? ""
  ).filter(Boolean);

  const fetchCopyProcedureOptions = fetchCopyProcedureList.map((item) =>
    item?.proceduremaster?.procedure ?? item?.procedure ?? ""
  ).filter(Boolean);
  const fetchCopyDocumentNameOptions = fetchCopyDocumentNameList.map((d) =>
    typeof d === "string" ? d : d.documentName ?? d.name ?? d.value ?? ""
  ).filter(Boolean);

  return (
    <div className="w-full bg-[#F3FAF7] rounded-2xl p-8 min-h-[80vh]">
      {/* Header */}
      <div className="flex items-center justify-between mt-16">
        <h1 className="font-lufga font-normal text-[40px] tracking-[-0.04em] text-black">
          Procedure - Document Mapping
        </h1>

        <button 
          onClick={handleReset}
          className="rounded-[54px] px-[36px] py-[14px] bg-[#7AD3AE] border-[#5AC69F] border-solid font-lufga font-medium text-[15px] text-black hover:bg-[#5AC69F] transition-colors cursor-pointer"
        >
          Reset
        </button>
      </div>

      {/* Top Filters */}
      <div className="flex flex-wrap gap-6 mb-8 font-lufga font-regular">
        <FilterDropdown
          placeholder="Scheme"
          options={schemeOptions}
          disabled={loadingSchemes}
          value={selectedScheme}
          setValue={setSelectedScheme}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Speciality"
          options={specialityTypeOptions}
          value={selectedSpecialty}
          setValue={setSelectedSpecialty}
          disabled={loadingSpecialties}
          clearValue={null}
          enableSearch={true}
          labelMap={specialityLabelMap}
          valueTitle={selectedSpecialty ? (specialityLabelMap[selectedSpecialty] || selectedSpecialty) : undefined}
        />
        <FilterDropdown
          placeholder={
            !selectedScheme || !selectedSpecialty
              ? "Select scheme and speciality first"
              : loadingProcedures
                ? "Loading..."
                : "Procedure"
          }
          options={procedureOptions}
          disabled={!selectedScheme || !selectedSpecialty || loadingProcedures}
          value={selectedProcedure}
          setValue={setSelectedProcedure}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Document Type"
          options={documentTypeOptions}
          disabled={loadingDocumentTypes}
          value={selectedDocumentType}
          setValue={setSelectedDocumentType}
          labelMap={documentTypeLabelMap}
        />
        <FilterDropdown
          placeholder={
            !selectedDocumentType
              ? "Select document type first"
              : loadingDocumentNames
                ? "Loading..."
                : "Document Name"
          }
          options={documentNameOptions}
          disabled={!selectedDocumentType || loadingDocumentNames}
          value={selectedDocumentName}
          setValue={handleDocumentNameSelect}
          enableSearch={true}
          keepOpenOnSelect={true}
        />
      </div>

      {/* Save button below first filters: only when document selected and NOT in two-column layout */}
      {!showFetched && pendingDocumentsToMap.length > 0 && (
        <div className="flex justify-start mb-6">
          <button
            type="button"
            onClick={handleSaveMappingFromHeader}
            disabled={savingFromHeader || savingFreeze || !selectedProcedure}
            className="rounded-[54px] px-[80px] py-[18px] bg-[#7AD3AE] font-lufga font-medium text-[16px] text-black border-[#5AC69F] border-solid disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {savingFromHeader && (
              <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {savingFromHeader ? "Saving..." : "Save"}
          </button>
        </div>
      )}

      {loadingMappedDocs && (
        <p className="font-lufga text-[14px] text-gray-500 mb-4">Loading documents…</p>
      )}

      {/* Selected from header: chips for documents to be mapped on Save */}
      {pendingDocumentsToMap.length > 0 && (
        <div className="sticky top-0 z-40 border border-[#E8E8E8] rounded-2xl bg-white px-4 py-3 mb-6 shadow-md">
          <div className="flex items-center justify-between mb-2">
            <div className="font-lufga text-[12px] font-medium text-gray-500">
              Selected Documents ({pendingDocumentsToMap.length})
            </div>
            <button
              type="button"
              onClick={() => setPendingDocumentsToMap([])}
              className="font-lufga text-[13px] font-medium text-red-600 hover:text-red-800 cursor-pointer px-3 py-1 rounded-lg hover:bg-red-50 transition-colors"
              title="Clear All"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
            {pendingDocumentsToMap.map((item, index) => {
              const label =
                typeof item.doc === "string"
                  ? item.doc
                  : item.doc?.documentName ?? item.doc?.name ?? item.doc?.value ?? "—";
              return (
                <span
                  key={`${getDocMappingId(item.doc) ?? index}-${index}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4FBF8] border border-[#CDEFE2] rounded-2xl font-lufga text-[13px] text-[#1F6B4B] max-w-full min-w-0"
                >
                  <span className="break-words text-left flex-1 min-w-0">{label}</span>
                  <button
                    type="button"
                    onClick={() => removePendingDocument(index)}
                    className="cursor-pointer flex-shrink-0 text-gray-500 hover:text-black font-lufga text-[16px] leading-none"
                    title="Unselect"
                    aria-label="Unselect"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Single-column: Default Mapped Documents (only when NOT in two-column layout) */}
      {!showFetched && (
        <div className="mb-8 font-lufga font-regular">
          <h2 className="font-lufga text-[28px] mb-6 font-medium">
            Default Mapped Documents
          </h2>
          {loadingMappedDocs && (
            <p className="text-gray-500 text-sm mb-4">Loading mapped documents…</p>
          )}
          {shouldShowSection("admission") && (
            <Section
              title="ADMISSION"
              items={admissionDocuments}
              onAdd={() => handleAddClickForSection("admission")}
              onDelete={(index) => handleDeleteFromSection("admission", index)}
            />
          )}
          {shouldShowSection("enhancement") && (
            <Section
              title="ENHANCEMENT"
              items={enhancementDocuments}
              onAdd={() => handleAddClickForSection("enhancement")}
              onDelete={(index) => handleDeleteFromSection("enhancement", index)}
            />
          )}
          {shouldShowSection("discharge") && (
            <Section
              title="DISCHARGE / CLAIM"
              items={dischargeDocuments}
              onAdd={() => handleAddClickForSection("discharge")}
              onDelete={(index) => handleDeleteFromSection("discharge", index)}
            />
          )}
        </div>
      )}

      {/* Fetch and Copy Documents from existing – same filters as header, independent state */}
      <div className="bg-white rounded-2xl p-6 flex flex-wrap items-center gap-6 mb-10">
        <span className="font-lufga font-normal text-[15px] text-gray-700">
          Fetch and Copy Documents from existing :
        </span>
        <FilterDropdown
          placeholder="Scheme"
          options={schemeOptions}
          disabled={loadingSchemes}
          value={fetchCopyScheme}
          setValue={setFetchCopyScheme}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Speciality"
          options={specialityTypeOptions}
          value={fetchCopySpecialty}
          setValue={setFetchCopySpecialty}
          disabled={loadingSpecialties}
          clearValue={null}
          enableSearch={true}
          labelMap={specialityLabelMap}
          valueTitle={fetchCopySpecialty ? (specialityLabelMap[fetchCopySpecialty] || fetchCopySpecialty) : undefined}
        />
        <FilterDropdown
          placeholder={
            !fetchCopyScheme || !fetchCopySpecialty
              ? "Select scheme and speciality first"
              : loadingFetchCopyProcedures
                ? "Loading..."
                : "Procedure"
          }
          options={fetchCopyProcedureOptions}
          disabled={!fetchCopyScheme || !fetchCopySpecialty || loadingFetchCopyProcedures}
          value={fetchCopyProcedure}
          setValue={setFetchCopyProcedure}
          enableSearch={true}
        />
        <FilterDropdown
          placeholder="Document Type"
          options={documentTypeOptions}
          disabled={loadingDocumentTypes}
          value={fetchCopyDocumentType}
          setValue={setFetchCopyDocumentType}
          labelMap={documentTypeLabelMap}
        />
        <FilterDropdown
          placeholder={
            !fetchCopyDocumentType
              ? "Select document type first"
              : loadingFetchCopyDocumentNames
                ? "Loading..."
                : "Document Name"
          }
          options={fetchCopyDocumentNameOptions}
          disabled={!fetchCopyDocumentType || loadingFetchCopyDocumentNames}
          value={fetchCopyDocumentName}
          setValue={setFetchCopyDocumentName}
          enableSearch={true}
        />
        <button
          type="button"
          onClick={fetchFetchCopyMappedDocs}
          disabled={!fetchCopyProcedure || loadingFetchCopyDocs}
          className="ml-auto rounded-[54px] px-[36px] py-[14px] bg-[#7AD3AE] font-lufga font-medium text-[15px] text-black border-[#5AC69F] border-solid disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {loadingFetchCopyDocs && (
            <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {loadingFetchCopyDocs ? "Fetching..." : "Fetch and copy"}
        </button>
      </div>

      {/* Two-column layout: left = Default Mapped Documents (header), right = Fetched from Fetch and Copy filters */}
      {showFetched && (
        <>
          <div className="grid grid-cols-2 gap-12 mt-10">
            {/* LEFT: Default Mapped Documents (from header filters) – aligned with right by minHeight */}
            <div>
              <h2 className="font-lufga font-medium text-[28px] mb-8">
                Default Mapped Documents
              </h2>
              {shouldShowSection("admission") && (
                <Section
                  title="ADMISSION"
                  items={admissionDocuments}
                  onAdd={() => handleAddClickForSection("admission")}
                  onDelete={(index) => handleDeleteFromSection("admission", index)}
                  minHeight="320px"
                />
              )}
              {shouldShowSection("enhancement") && (
                <Section
                  title="ENHANCEMENT"
                  items={enhancementDocuments}
                  onAdd={() => handleAddClickForSection("enhancement")}
                  onDelete={(index) => handleDeleteFromSection("enhancement", index)}
                  minHeight="320px"
                />
              )}
              {shouldShowSection("discharge") && (
                <Section
                  title="DISCHARGE / CLAIM"
                  items={dischargeDocuments}
                  onAdd={() => handleAddClickForSection("discharge")}
                  onDelete={(index) => handleDeleteFromSection("discharge", index)}
                  minHeight="320px"
                />
              )}
            </div>

            {/* RIGHT: Fetch and Copy Documents from existing (from Fetch and Copy filters) */}
            <div>
              <h2 className="font-lufga font-medium text-[22px] text-gray-700 mb-8">
                Fetch and Copy Documents from existing
              </h2>
              {shouldShowSection("admission") && (
                <FetchCopySection
                  title="ADMISSION"
                  documents={fetchCopyAdmissionDocuments}
                  existingDocuments={admissionDocuments}
                  getDocId={getDocMappingId}
                  selectedIds={selectedFetchCopyAdmissionIds}
                  onToggle={(doc) => handleToggleFetchCopySelection("admission", doc)}
                  onCopy={(doc) => handleCopyFromFetchCopy("admission", doc)}
                  onMergeAll={() => handleMergeSection("admission")}
                  minHeight="320px"
                />
              )}
              {shouldShowSection("enhancement") && (
                <FetchCopySection
                  title="ENHANCEMENT"
                  documents={fetchCopyEnhancementDocuments}
                  existingDocuments={enhancementDocuments}
                  getDocId={getDocMappingId}
                  selectedIds={selectedFetchCopyEnhancementIds}
                  onToggle={(doc) => handleToggleFetchCopySelection("enhancement", doc)}
                  onCopy={(doc) => handleCopyFromFetchCopy("enhancement", doc)}
                  onMergeAll={() => handleMergeSection("enhancement")}
                  minHeight="320px"
                />
              )}
              {shouldShowSection("discharge") && (
                <FetchCopySection
                  title="DISCHARGE / CLAIM"
                  documents={fetchCopyDischargeDocuments}
                  existingDocuments={dischargeDocuments}
                  getDocId={getDocMappingId}
                  selectedIds={selectedFetchCopyDischargeIds}
                  onToggle={(doc) => handleToggleFetchCopySelection("discharge", doc)}
                  onCopy={(doc) => handleCopyFromFetchCopy("discharge", doc)}
                  onMergeAll={() => handleMergeSection("discharge")}
                  minHeight="320px"
                />
              )}
            </div>
          </div>

          <div className="flex justify-center mt-12">
            <button
              type="button"
              onClick={handleMergeAll}
              className="border-2 border-[#7AD3AE] rounded-full px-12 py-4 font-lufga text-[16px] flex items-center gap-2 hover:opacity-90"
            >
              <img src={mergeIcon} alt="merge" className="w-5 h-5" />
              Merge All
            </button>
          </div>
        </>
      )}

      {/* Bottom Buttons: only when fetching from both sets (two-column layout) */}
      {showFetched && (
        <div className="flex justify-center mt-6 gap-4">
          <button
            type="button"
            onClick={handleSaveAndFreeze}
            disabled={savingFreeze || savingFromHeader}
            className="rounded-[54px] px-[28px] py-[14px] bg-[#7AD3AE] font-lufga font-medium text-[15px] text-black border-[#5AC69F] border-solid disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {savingFreeze && (
              <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {savingFreeze ? "Saving and freezing..." : "Save and Freeze"}
          </button>
          <button
            type="button"
            onClick={handleSaveMappingFromHeader}
            disabled={savingFromHeader || savingFreeze}
            className="rounded-[54px] px-[80px] py-[18px] bg-[#7AD3AE] font-lufga font-medium text-[16px] text-black border-[#5AC69F] border-solid disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {savingFromHeader && (
              <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {savingFromHeader ? "Saving..." : "Save"}
          </button>
        </div>
      )}

      <AddProcedureDocumentModal
        open={openAddDocumentModal}
        onClose={() => {
          setOpenAddDocumentModal(false);
          setAddDocumentSection(null);
          setSelectedDocumentsForAdd([]);
        }}
        section={addDocumentSection}
        selectedDocuments={selectedDocumentsForAdd}
        setSelectedDocuments={setSelectedDocumentsForAdd}
        schemeOptions={schemeOptions}
        selectedScheme={selectedScheme}
        setSelectedScheme={setSelectedScheme}
        loadingSchemes={loadingSchemes}
        specialityTypeOptions={specialityTypeOptions}
        specialityLabelMap={specialityLabelMap}
        selectedSpecialty={selectedSpecialty}
        setSelectedSpecialty={setSelectedSpecialty}
        procedureOptions={procedureOptions}
        selectedProcedure={selectedProcedure}
        setSelectedProcedure={setSelectedProcedure}
        loadingProcedures={loadingProcedures}
        documentTypeOptions={documentTypeOptions}
        documentTypeLabelMap={documentTypeLabelMap}
        selectedDocumentType={selectedDocumentType}
        setSelectedDocumentType={setSelectedDocumentType}
        loadingDocumentTypes={loadingDocumentTypes}
        documentNameList={documentNameList}
        loadingDocumentNames={loadingDocumentNames}
        saving={savingMapping}
        onAdd={handleAddDocumentsToSection}
      />

      <ConfirmRemoveMapping
        open={openConfirmRemoveModal}
        onClose={() => {
          setOpenConfirmRemoveModal(false);
          setSelectedToRemove([]);
        }}
        onConfirm={handleConfirmRemoveMapping}
        loading={removingMapping}
      />
    </div>
  );
}

function SelectField({ label, options = [], onSelect }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative min-w-[220px]">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between h-[56px] rounded-[100px] px-[24px] bg-white border-2 border-[#CFCFCF] font-lufga font-normal text-[15px] text-gray-700 cursor-pointer"
      >
        {label}
        <img src={downIcon} alt="dropdown" className="w-4 h-4" />
      </div>

      {open && options.length > 0 && (
        <div className="absolute top-[60px] w-full bg-white rounded-xl shadow-lg z-20">
          {options.map((item) => (
            <div
              key={item}
              onClick={() => {
                onSelect?.(item);
                setOpen(false);
              }}
              className="px-4 py-3 hover:bg-[#F3FAF7] cursor-pointer text-sm"
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, items = [], onAdd, onDelete, minHeight }) {
  const isEmpty = !Array.isArray(items) || items.length === 0;
  const displayItems = Array.isArray(items) ? items : [];

  return (
    <div className="mb-10" style={minHeight ? { minHeight } : undefined}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-lufga font-semibold text-[16px]">{title}</h3>
        <button
          type="button"
          onClick={() => typeof onAdd === "function" && onAdd()}
          className="w-[36px] h-[36px] flex items-center justify-center hover:opacity-90 p-0 border-0 bg-transparent"
          title="Add document"
        >
          <img src={addButtonSymbol} alt="add" className="w-full h-full object-contain" />
        </button>
      </div>

      <div className="max-h-[280px] overflow-y-auto rounded-xl border border-[#E8E8E8] bg-[#F3FAF7]">
        {isEmpty ? (
          <div className="py-6 px-4 rounded-xl border-0 border-dashed border-gray-200 bg-[#F3FAF7]">
            <p className="font-lufga text-[14px] text-gray-500 text-center">
              No documents mapped
            </p>
          </div>
        ) : (
          displayItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start justify-between gap-6 px-4 py-1.5 border-b border-[#F0F0F0] last:border-b-0"
            >
              <p className="font-lufga text-[14px] leading-[155%] max-w-[90%] break-words">
                {typeof item === "string" ? item : item?.documentName ?? item?.name ?? item?.value ?? "—"}
              </p>
              <img
                src={deleteIcon}
                alt="delete"
                className="w-5 h-5 cursor-pointer flex-shrink-0"
                onClick={() => typeof onDelete === "function" && onDelete(index)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FetchCopySection({
  title,
  documents = [],
  existingDocuments = [],
  getDocId,
  selectedIds = new Set(),
  onToggle,
  onCopy,
  onMergeAll,
  minHeight,
}) {
  const getDocLabel = (doc) =>
    typeof doc === "string"
      ? doc
      : doc?.documenttypemapping?.documentName ??
        doc?.documentName ??
        doc?.name ??
        doc?.documentTitle ??
        doc?.label ??
        doc?.value ??
        doc?.title ??
        "—";

  const existingIds = new Set(
    (Array.isArray(existingDocuments) ? existingDocuments : [])
      .map((d) => (getDocId ? getDocId(d) : ""))
      .filter(Boolean),
  );

  const isAlreadyPresent = (doc) => {
    const id = getDocId ? getDocId(doc) : "";
    return id ? existingIds.has(id) : false;
  };

  const isEmpty = !Array.isArray(documents) || documents.length === 0;
  const selectedCount = Array.isArray(documents)
    ? documents.filter((d) => !isAlreadyPresent(d) && selectedIds.has(getDocId ? getDocId(d) : "")).length
    : 0;
  const canMergeAll = typeof onMergeAll === "function" && selectedCount > 0;
  return (
    <div
      className="mb-10"
      style={minHeight ? { minHeight } : undefined}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-lufga font-semibold text-[16px]">
          {title} ({documents.length})
        </h3>
        <button
          type="button"
          onClick={onMergeAll}
          disabled={!canMergeAll}
          title={`Merge selected ${title} documents to Default Mapped Documents`}
          className="flex items-center gap-1.5 rounded-full border border-[#5AC69F] px-3 py-1.5 font-lufga text-[13px] text-[#5AC69F] hover:bg-[#F4FBF8] disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
        >
          <img src={mergeIcon} alt="merge" className="w-4 h-4" />
          Merge all
        </button>
      </div>
      <div className="max-h-[280px] overflow-y-auto rounded-xl border border-[#E8E8E8] bg-[#F3FAF7]">
        {isEmpty ? (
          <div className="py-6 px-4 rounded-xl border-0 border-dashed border-gray-200 bg-[#F3FAF7]">
            <p className="font-lufga text-[14px] text-gray-500 text-center">
              No documents
            </p>
          </div>
        ) : (
          documents.map((doc, index) => {
            const id = getDocId ? getDocId(doc) : "";
            const alreadyExists = isAlreadyPresent(doc);
            const selected = !alreadyExists && id && selectedIds.has(id);
            return (
              <div
                key={id || index}
                className={`flex items-start gap-3 px-4 py-1.5 border-b border-[#F0F0F0] last:border-b-0 ${alreadyExists ? "bg-[#E8F5E9]/60" : selected ? "bg-[#F4FBF8]" : ""}`}
              >
                {alreadyExists ? (
                  /* Already mapped: green check icon */
                  <span
                    className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-[#4CAF50] flex items-center justify-center"
                    title="Already present in mapped documents"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : (
                  /* Not mapped: selection checkbox */
                  <button
                    type="button"
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-2 border-gray-300 hover:border-[#5AC69F] focus:outline-none focus:ring-2 focus:ring-[#5AC69F] mt-0.5"
                    style={selected ? { backgroundColor: "#38B58A", borderColor: "#38B58A" } : {}}
                    onClick={() => typeof onToggle === "function" && onToggle(doc)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (typeof onToggle === "function") onToggle(doc);
                      }
                    }}
                    title={selected ? "Unselect" : "Select for merge"}
                    aria-label={selected ? "Unselect" : "Select for merge"}
                  >
                    {selected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )}
                <p className={`font-lufga text-[14px] leading-[155%] flex-1 min-w-0 break-words ${alreadyExists ? "text-gray-500" : ""}`}>
                  {getDocLabel(doc)}
                </p>
                {alreadyExists ? (
                  <span
                    className="flex-shrink-0 font-lufga text-[11px] text-[#4CAF50] bg-[#E8F5E9] border border-[#4CAF50]/30 rounded-full px-2.5 py-1 whitespace-nowrap mt-0.5"
                    title="This document is already mapped"
                  >
                    Already mapped
                  </span>
                ) : (
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer hover:bg-[#F4FBF8] hover:border-[#5AC69F] hover:text-[#5AC69F]"
                    title="Copy to Default Mapped Documents"
                    role="button"
                    tabIndex={0}
                    onClick={() => typeof onCopy === "function" && onCopy(doc)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (typeof onCopy === "function") onCopy(doc);
                      }
                    }}
                  >
                    <img src={mergeIcon} alt="copy" className="w-4 h-4" />
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function AddProcedureDocumentModal({
  open,
  onClose,
  section,
  selectedDocuments,
  setSelectedDocuments,
  schemeOptions,
  selectedScheme,
  setSelectedScheme,
  loadingSchemes,
  specialityTypeOptions,
  specialityLabelMap = {},
  selectedSpecialty,
  setSelectedSpecialty,
  procedureOptions,
  selectedProcedure,
  setSelectedProcedure,
  loadingProcedures,
  documentTypeOptions,
  documentTypeLabelMap,
  selectedDocumentType,
  setSelectedDocumentType,
  loadingDocumentTypes,
  documentNameList,
  loadingDocumentNames,
  saving,
  onAdd,
}) {
  const [search, setSearch] = useState("");
  const prevOpenRef = useRef(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    if (open && !prevOpenRef.current) {
      setSearch("");
      setSelectedDocuments([]);
      prevOpenRef.current = true;
    }
    if (!open) prevOpenRef.current = false;
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open, setSelectedDocuments]);

  if (!open) return null;

  const sectionTitle =
    section === "admission"
      ? "ADMISSION"
      : section === "enhancement"
        ? "ENHANCEMENT"
        : section === "discharge"
          ? "DISCHARGE / CLAIM"
          : "";

  const getDocLabel = (d) =>
    typeof d === "string" ? d : d?.documentName ?? d?.name ?? d?.value ?? "";

  const getDocId = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d;
    const id =
      d.documentTypeMappingId ??
      d.documentId ??
      d.id ??
      (d.documentName && d.documentSubType
        ? `${d.documentName}|${d.documentSubType}`
        : d.documentName ?? d.name ?? "");
    return String(id || "");
  };

  const filteredList = (documentNameList || []).filter((d) =>
    getDocLabel(d).toLowerCase().includes((search || "").toLowerCase()),
  );

  const isSelected = (doc) => {
    if (!Array.isArray(selectedDocuments) || selectedDocuments.length === 0)
      return false;
    const id = getDocId(doc);
    if (!id) return false;
    return selectedDocuments.some((d) => getDocId(d) === id);
  };

  const toggleDoc = (doc) => {
    const id = getDocId(doc);
    if (!id) return;
    setSelectedDocuments((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const exists = list.some((d) => getDocId(d) === id);
      if (exists) {
        return list.filter((d) => getDocId(d) !== id);
      }
      return [...list, doc];
    });
  };

  const removeDoc = (doc) => {
    const id = getDocId(doc);
    setSelectedDocuments((prev) =>
      (Array.isArray(prev) ? prev : []).filter((d) => getDocId(d) !== id),
    );
  };

  const canAdd =
    section &&
    Array.isArray(selectedDocuments) &&
    selectedDocuments.length > 0 &&
    !loadingDocumentNames &&
    !saving;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#00000066] px-4">
      <div className="relative w-[80vw] max-w-[80vw] bg-white rounded-[24px] px-8 py-8 max-h-[80vh] flex flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-8 right-6 p-0 border-none bg-transparent hover:opacity-70 transition-opacity"
        >
          <span className="text-xl leading-none">×</span>
        </button>

        <h2 className="font-lufga font-medium text-[20px] text-[#494949] pr-10">
          Add document to {sectionTitle}
        </h2>

        <p className="font-lufga text-[14px] text-gray-500 mt-1 mb-4">
          Choose filters and select one or more documents to add to this section.
        </p>

        <div className="flex flex-wrap gap-4 mb-4">
          <FilterDropdown
            placeholder="Scheme"
            options={schemeOptions}
            disabled={loadingSchemes}
            value={selectedScheme}
            setValue={setSelectedScheme}
          />
          <FilterDropdown
            placeholder="Speciality"
            options={specialityTypeOptions}
            value={selectedSpecialty}
            setValue={setSelectedSpecialty}
            clearValue={null}
            labelMap={specialityLabelMap}
            valueTitle={selectedSpecialty ? (specialityLabelMap[selectedSpecialty] || selectedSpecialty) : undefined}
          />
          <FilterDropdown
            placeholder={
              !selectedScheme || !selectedSpecialty
                ? "Select scheme and speciality first"
                : loadingProcedures
                  ? "Loading procedures..."
                  : "Procedure"
            }
            options={procedureOptions}
            value={selectedProcedure}
            setValue={setSelectedProcedure}
            disabled={!selectedScheme || !selectedSpecialty || loadingProcedures}
          />
          <FilterDropdown
            placeholder="Document Type"
            options={documentTypeOptions}
            disabled={loadingDocumentTypes}
            value={selectedDocumentType}
            setValue={setSelectedDocumentType}
            labelMap={documentTypeLabelMap}
          />
        </div>

        {Array.isArray(selectedDocuments) && selectedDocuments.length > 0 && (
          <div className="mb-4">
            <p className="font-lufga text-[13px] font-medium text-gray-600 mb-2">
              Selected documents ({selectedDocuments.length})
            </p>
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto p-2 bg-[#F4FBF8] rounded-xl border border-[#CDEFE2]">
            {selectedDocuments.map((doc, index) => {
              const label = getDocLabel(doc);
              const id = getDocId(doc);
              return (
                <span
                  key={`${id || label}-${index}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F4FBF8] border border-[#CDEFE2] font-lufga text-[12px] text-[#1F6B4B] max-w-full"
                >
                  <span className="truncate max-w-[220px]">{label || "—"}</span>
                  <button
                    type="button"
                    onClick={() => removeDoc(doc)}
                    className="ml-1 text-xs text-[#1F6B4B] hover:text-[#0F3A29]"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {!selectedDocumentType ? (
            <p className="font-lufga text-[14px] text-gray-500 py-4">
              Select a document type to see documents.
            </p>
          ) : (
            <>
              <div className="relative mb-3 flex-shrink-0">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search document name..."
                  className="w-full px-4 py-3 rounded-full border border-[#E8E8E8] font-lufga text-[14px]"
                />
              </div>

              <div className="border border-[#E8E8E8] rounded-xl overflow-hidden flex-1 overflow-y-auto min-h-0">
              {loadingDocumentNames ? (
                <div className="py-12 text-center font-lufga text-[14px] text-gray-500">
                  Loading documents...
                </div>
              ) : filteredList.length === 0 ? (
                <div className="py-12 text-center font-lufga text-[14px] text-gray-500">
                  No documents found.
                </div>
              ) : (
                filteredList.map((doc, index) => {
                  const label = getDocLabel(doc);
                  const selected = isSelected(doc);
                  return (
                    <div
                      key={`${label}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDoc(doc)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleDoc(doc);
                        }
                      }}
                      className={`px-4 py-1.5 font-lufga text-[14px] leading-[155%] cursor-pointer border-b border-[#F0F0F0] last:border-b-0 break-words ${
                        selected ? "bg-[#F4FBF8] text-[#38B58A]" : "hover:bg-[#F9F9F9]"
                      }`}
                    >
                      {label || "—"}
                    </div>
                  );
                })
              )}
              </div>

              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-[#E8E8E8] flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full px-6 py-3 font-lufga text-[15px] border border-[#E8E8E8] bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onAdd}
                  disabled={!canAdd}
                  className="rounded-full px-6 py-3 font-lufga text-[15px] bg-[#7AD3AE] border border-[#5AC69F] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5AC69F] transition-colors"
                >
                  {saving ? "Saving..." : `Add to ${sectionTitle}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
